/**
 * useJobHandlers - Custom hook containing all job-related handlers
 * Extracts business logic from JobDetailPage for better maintainability
 */
import { useCallback, useRef } from "react";
import apiClient from "../services/api";
import { toast } from "sonner";
import { formatCurrency } from "../lib/formatters";
import { buildBiddingGCs, getBiddingGCsDisplayName } from "../lib/biddingGcUtils";
export const useJobHandlers = ({ jobId, getAuthHeaders, setJob, fetchData, navigate }) => {
  
  // Ref to hold handlePopulateFromBidSheet so it can be called from handleContractReceived
  const populateRef = useRef(null);

  // ============= JOB ACTIONS =============
  const handleDelete = useCallback(async () => {
    try {
      await apiClient.delete(`/jobs/${jobId}`);
      toast.success("Job deleted successfully");
      navigate("/jobs");
    } catch (error) {
      toast.error("Failed to delete job");
    }
  }, [jobId, getAuthHeaders, navigate]);

  const handleStatusChange = useCallback(async (status, options = {}) => {
    try {
      const payload = { status };
      if (options.clearContract) {
        payload.contract_received_date = null;
        payload.contracted_gc_id = "";
        payload.general_contractor_id = "";
        // Unlock all bid sheets so they can be edited again
        const currentJob = typeof options.job === 'object' ? options.job : null;
        if (currentJob && currentJob.bid_sheets) {
          payload.bid_sheets = currentJob.bid_sheets.map(bs => ({
            ...bs,
            is_locked: false,
          }));
        }
      }
      const response = await apiClient.put(`/jobs/${jobId}`, payload);
      setJob(response.data);
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleEditJob = useCallback(async (editForm, generalContractors) => {
    try {
      const { bidding_gc_ids, bidding_gcs: existingGCs, ...formFields } = editForm;
      const payload = { ...formFields, contract_amount: parseFloat(formFields.contract_amount) || 0 };
      
      if (bidding_gc_ids && bidding_gc_ids.length > 0) {
        payload.bidding_gcs = buildBiddingGCs(bidding_gc_ids, existingGCs || [], generalContractors);
        payload.client_name = getBiddingGCsDisplayName(payload.bidding_gcs);
        // For bidding jobs, general_contractor_id is not relevant
        delete payload.general_contractor_id;
      } else if (formFields.general_contractor_id) {
        const gc = generalContractors.find(g => g.id === formFields.general_contractor_id);
        if (gc) payload.client_name = gc.name;
      }
      
      const response = await apiClient.put(`/jobs/${jobId}`, payload);
      setJob(response.data);
      toast.success("Job updated successfully");
      return true;
    } catch (error) {
      toast.error("Failed to update job");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleContractReceived = useCallback(async (job, { receivedDate, acceptedBidIds, contractAmount, contractedGcId, gcAddress, contractNumber, projectNumber, billingDate }) => {
    try {
      const updatedBidSheets = (job.bid_sheets || []).map(bs => {
        const isChangeOrder = bs.bid_type === "change_order" || bs.is_change_order;
        if (isChangeOrder) return bs;
        return { ...bs, is_locked: true, is_accepted: acceptedBidIds.includes(bs.id) };
      });

      // Build update payload
      const updatePayload = {
        status: "precon",
        contract_received_date: receivedDate,
        accepted_bid_ids: acceptedBidIds,
        contract_amount: contractAmount,
        bid_sheets: updatedBidSheets,
      };

      // Add contract details from dialog
      if (contractNumber) updatePayload.contract_number = contractNumber;
      if (projectNumber) updatePayload.project_number = projectNumber;
      if (billingDate) updatePayload.billing_date = billingDate;

      // If a contracted GC was selected, set it and auto-fill from GC info
      if (contractedGcId) {
        updatePayload.contracted_gc_id = contractedGcId;
        updatePayload.general_contractor_id = contractedGcId;
        const biddingGCs = job.bidding_gcs || [];
        const contractedGc = biddingGCs.find((gc) => gc.gc_id === contractedGcId);
        if (contractedGc) {
          updatePayload.client_name = contractedGc.gc_name;
        }
      }

      const response = await apiClient.put(
        `/jobs/${jobId}`,
        updatePayload
      );
      
      setJob(response.data);
      toast.success(`Contract received! Amount: $${contractAmount.toLocaleString()}`);
      
      // Auto-repopulate estimates from only accepted bid sheets
      if (populateRef.current) {
        await populateRef.current(response.data);
      }
      
      return true;
    } catch (error) {
      toast.error("Failed to process contract");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  // ============= LABOR HANDLERS =============
  const handleSaveLaborEstimates = useCallback(async (estimates) => {
    try {
      const response = await apiClient.put(`/jobs/${jobId}`, { labor_estimates: estimates });
      setJob(response.data);
      toast.success("Labor estimate updated");
      return true;
    } catch (error) {
      toast.error("Failed to save estimate");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  // ============= BID SHEET HANDLERS =============
  const handleSaveBidSheets = useCallback(async (bidSheets) => {
    try {
      const response = await apiClient.put(`/jobs/${jobId}`, { bid_sheets: bidSheets });
      setJob(response.data);
      toast.success("Bid sheet saved");
      return true;
    } catch (error) {
      toast.error("Failed to save bid sheet");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleApproveCOBidSheet = useCallback(async (sheetId, approvalDate) => {
    try {
      const response = await apiClient.post(
        `/jobs/${jobId}/bid-sheets/${sheetId}/approve`,
        { approval_date: approvalDate }
      );
      setJob(response.data);
      toast.success("Change Order approved and synced");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve change order");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleRevokeCOApproval = useCallback(async (sheetId) => {
    try {
      const response = await apiClient.post(
        `/jobs/${jobId}/bid-sheets/${sheetId}/revoke-approval`);
      setJob(response.data);
      toast.success("Change Order approval revoked");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke approval");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  // ============= MATERIAL HANDLERS =============
  const handleSaveMaterialEstimates = useCallback(async (estimates) => {
    try {
      const response = await apiClient.put(`/jobs/${jobId}`, { material_estimates: estimates });
      setJob(response.data);
      toast.success("Material estimate updated");
      return true;
    } catch (error) {
      toast.error("Failed to save estimate");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  // ============= INVOICE HANDLERS =============
  const handleAddInvoice = useCallback(async (data) => {
    try {
      const response = await apiClient.post(`/jobs/${jobId}/invoices`, data);
      setJob(response.data);
      toast.success("Invoice added");
      return true;
    } catch (error) {
      toast.error("Failed to add invoice");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleEditInvoice = useCallback(async (invoiceId, data) => {
    try {
      const response = await apiClient.put(`/jobs/${jobId}/invoices/${invoiceId}`, data);
      setJob(response.data);
      toast.success("Invoice updated");
      return true;
    } catch (error) {
      toast.error("Failed to update invoice");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleDeleteInvoice = useCallback(async (invoiceId) => {
    try {
      const response = await apiClient.delete(`/jobs/${jobId}/invoices/${invoiceId}`);
      setJob(response.data);
      toast.success("Invoice deleted");
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleToggleInvoicePaid = useCallback(async (inv) => {
    try {
      const response = await apiClient.put(`/jobs/${jobId}/invoices/${inv.id}`, { paid: !inv.paid });
      setJob(response.data);
      toast.success(inv.paid ? "Invoice marked unpaid" : "Invoice marked paid");
    } catch (error) {
      toast.error("Failed to update invoice");
    }
  }, [jobId, getAuthHeaders, setJob]);

  // ============= CHANGE ORDER HANDLERS =============
  const handleAddChangeOrder = useCallback(async (data) => {
    try {
      const response = await apiClient.post(`/jobs/${jobId}/change-orders`, data);
      setJob(response.data);
      toast.success("Change order added");
      return true;
    } catch (error) {
      toast.error("Failed to add change order");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleEditChangeOrder = useCallback(async (coId, data) => {
    try {
      const response = await apiClient.put(`/jobs/${jobId}/change-orders/${coId}`, data);
      setJob(response.data);
      toast.success("Change order updated");
      return true;
    } catch (error) {
      toast.error("Failed to update change order");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleDeleteChangeOrder = useCallback(async (coId) => {
    try {
      const response = await apiClient.delete(`/jobs/${jobId}/change-orders/${coId}`);
      setJob(response.data);
      toast.success("Change order deleted");
    } catch (error) {
      toast.error("Failed to delete change order");
    }
  }, [jobId, getAuthHeaders, setJob]);

  // ============= BILLING HANDLERS =============
  const handleAddBilling = useCallback(async (data) => {
    try {
      const response = await apiClient.post(`/jobs/${jobId}/billings`, data);
      setJob(response.data);
      toast.success("Billing added");
      return true;
    } catch (error) {
      toast.error("Failed to add billing");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleEditBilling = useCallback(async (billingId, data) => {
    try {
      const response = await apiClient.put(`/jobs/${jobId}/billings/${billingId}`, data);
      setJob(response.data);
      toast.success("Billing updated");
      return true;
    } catch (error) {
      toast.error("Failed to update billing");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleDeleteBilling = useCallback(async (billingId) => {
    try {
      const response = await apiClient.delete(`/jobs/${jobId}/billings/${billingId}`);
      setJob(response.data);
      toast.success("Billing deleted");
    } catch (error) {
      toast.error("Failed to delete billing");
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleToggleBillingEdited = useCallback(async (b) => {
    try {
      const response = await apiClient.put(`/jobs/${jobId}/billings/${b.id}`, { edited: !b.edited });
      setJob(response.data);
    } catch (error) {
      toast.error("Failed to update billing");
    }
  }, [jobId, getAuthHeaders, setJob]);

  // ============= EQUIPMENT HANDLERS =============
  const handleSaveEquipmentEstimates = useCallback(async (estimates, job) => {
    const existingCOItems = (job.equipment_estimates || []).filter(e => e.is_change_order);
    const equipmentEstimates = [...estimates.filter(e => !e.is_change_order), ...existingCOItems];
    
    try {
      const response = await apiClient.put(`/jobs/${jobId}`, { equipment_estimates: equipmentEstimates });
      setJob(response.data);
      toast.success("Equipment estimates updated");
      return true;
    } catch (error) {
      toast.error("Failed to update equipment estimates");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  // ============= PHOTO HANDLERS =============
  const handlePhotoUpload = useCallback(async (e, setUploading) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedExts = ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"];
    const validFiles = files.filter(file => {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      // iOS HEIC from the photo library often reports an empty or "image/heic"
      // MIME type, so validate by extension OR any image/* MIME (matches backend).
      const isImage = allowedExts.includes(ext) || (file.type || "").startsWith("image/");
      if (!isImage) {
        toast.error(`${file.name}: Only image files allowed`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      e.target.value = "";
      return;
    }

    setUploading(true);
    const formData = new FormData();
    validFiles.forEach(file => formData.append("files", file));

    try {
      const res = await apiClient.post(`/jobs/${jobId}/photos`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const count = res.data.count || validFiles.length;
      toast.success(`${count} photo${count > 1 ? 's' : ''} uploaded`);
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(err => toast.error(err));
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photos");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }, [jobId, getAuthHeaders, fetchData]);

  const handleDeletePhoto = useCallback(async (photoId) => {
    try {
      await apiClient.delete(`/jobs/${jobId}/photos/${photoId}`);
      toast.success("Photo deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  }, [jobId, getAuthHeaders, fetchData]);

  const handleCaptionUpdate = useCallback(async (photoId, caption) => {
    try {
      await apiClient.put(`/jobs/${jobId}/photos/${photoId}/caption`, { caption });
      toast.success("Caption updated");
      fetchData();
      return true;
    } catch (error) {
      toast.error("Failed to update caption");
      return false;
    }
  }, [jobId, getAuthHeaders, fetchData]);

  // ============= NOTE HANDLERS =============
  const handleAddNote = useCallback(async (data) => {
    try {
      const response = await apiClient.post(`/jobs/${jobId}/notes`, data);
      setJob(response.data);
      toast.success("Note added");
      return true;
    } catch (error) {
      toast.error("Failed to add note");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleEditNote = useCallback(async (noteId, data) => {
    try {
      const response = await apiClient.put(`/jobs/${jobId}/notes/${noteId}`, data);
      setJob(response.data);
      toast.success("Note updated");
      return true;
    } catch (error) {
      toast.error("Failed to update note");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  const handleDeleteNote = useCallback(async (noteId) => {
    try {
      const response = await apiClient.delete(`/jobs/${jobId}/notes/${noteId}`);
      setJob(response.data);
      toast.success("Note deleted");
    } catch (error) {
      toast.error("Failed to delete note");
    }
  }, [jobId, getAuthHeaders, setJob]);

  // ============= BIDDING GCS =============
  const handleUpdateBiddingGCs = useCallback(async (biddingGCs) => {
    try {
      const response = await apiClient.put(
        `/jobs/${jobId}`,
        { bidding_gcs: biddingGCs }
      );
      setJob(response.data);
      toast.success("Bid submissions updated");
      return true;
    } catch (error) {
      toast.error("Failed to update bid submissions");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  // ============= POPULATE FROM BID SHEET =============
  const handlePopulateFromBidSheet = useCallback(async (job) => {
    const bidSheets = job.bid_sheets || [];
    const hasContractReceived = job.contract_received_date && job.status !== "bidding";
    
    const relevantBidSheets = bidSheets.filter(bs => {
      const isChangeOrder = bs.bid_type === "change_order" || bs.is_change_order;
      if (isChangeOrder) return false;
      // Exclude the auto-generated "Lump Sum Bid" sheet to avoid double-counting
      if (bs.name === "Lump Sum Bid") return false;
      if (hasContractReceived) return bs.is_accepted;
      return true;
    });
    
    if (relevantBidSheets.length === 0) {
      toast.error("No bid sheets found. Create one in the Estimating tab first.");
      return false;
    }

    // Aggregate values from all relevant bid sheets
    let materialSubtotal = 0, laborSubtotal = 0, equipmentSubtotal = 0;
    let totalBurdenAmount = 0, totalTaxAmount = 0, totalProfitAmount = 0, grandTotal = 0;

    for (const bidSheet of relevantBidSheets) {
      const materialItems = bidSheet.material_items || [];
      const materialDirectSubtotal = materialItems.reduce((sum, item) => sum + (item.total || 0), 0);
      const materialMortarSubtotal = materialItems.reduce((sum, item) => sum + (item.mortar_total || 0), 0);
      const materialsCardTotal = materialDirectSubtotal + materialMortarSubtotal;
      
      const miscItems = bidSheet.misc_items || [];
      const miscMaterialSubtotal = miscItems.reduce((sum, item) => sum + (item.material_cost || 0), 0);
      const miscMortarSubtotal = miscItems.reduce((sum, item) => sum + (item.mortar_cost || 0), 0);
      const bsMaterialSubtotal = materialsCardTotal + miscMaterialSubtotal + miscMortarSubtotal;
      
      const laborEntries = bidSheet.labor_entries || [];
      const bsLaborSubtotal = laborEntries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
      
      const equipmentItems = bidSheet.equipment_items || [];
      const equipmentDirectSubtotal = equipmentItems.reduce((sum, item) => sum + (item.total || 0), 0);
      const equipmentMaterialSubtotal = equipmentItems.reduce((sum, item) => sum + (item.material_cost || 0), 0);
      const bsEquipmentSubtotal = equipmentDirectSubtotal + equipmentMaterialSubtotal;
      
      const burdenPercent = bidSheet.burden_percent || 0;
      const profitPercent = bidSheet.profit_percent || 0;
      const salesTaxPercent = bidSheet.sales_tax_percent || 0;
      const taxExempt = bidSheet.tax_exempt || false;
      
      const bsBurdenAmount = bsLaborSubtotal * (burdenPercent / 100);
      const miscSubtotal = miscItems.reduce((sum, item) => sum + (item.total || 0), 0);
      const subtotalBeforeProfit = bsMaterialSubtotal + bsLaborSubtotal + bsEquipmentSubtotal + miscSubtotal + bsBurdenAmount;
      const bsProfitAmount = subtotalBeforeProfit * (profitPercent / 100);
      const bsTaxAmount = taxExempt ? 0 : bsMaterialSubtotal * (salesTaxPercent / 100);
      const bsGrandTotal = subtotalBeforeProfit + bsProfitAmount + bsTaxAmount;

      materialSubtotal += bsMaterialSubtotal;
      laborSubtotal += bsLaborSubtotal;
      equipmentSubtotal += bsEquipmentSubtotal;
      totalBurdenAmount += bsBurdenAmount;
      totalTaxAmount += bsTaxAmount;
      totalProfitAmount += bsProfitAmount;
      grandTotal += bsGrandTotal;
    }

    const totalBaseCosts = materialSubtotal + laborSubtotal + equipmentSubtotal;
    
    if (totalBaseCosts === 0) {
      toast.error("Bid sheets have no costs to populate.");
      return false;
    }

    const materialProportion = materialSubtotal / totalBaseCosts;
    const laborProportion = laborSubtotal / totalBaseCosts;
    const equipmentProportion = equipmentSubtotal / totalBaseCosts;

    const materialProfitAllocation = totalProfitAmount * materialProportion;
    const laborProfitAllocation = totalProfitAmount * laborProportion;
    const equipmentProfitAllocation = totalProfitAmount * equipmentProportion;

    let materialFinal = Math.round(materialSubtotal + totalTaxAmount + materialProfitAllocation);
    let laborFinal = Math.round(laborSubtotal + totalBurdenAmount + laborProfitAllocation);
    let equipmentFinal = Math.round(equipmentSubtotal + equipmentProfitAllocation);

    const roundedGrandTotal = Math.round(grandTotal);
    let difference = roundedGrandTotal - (materialFinal + laborFinal + equipmentFinal);

    if (difference !== 0) {
      const categories = [
        { name: 'labor', value: laborFinal },
        { name: 'material', value: materialFinal },
        { name: 'equipment', value: equipmentFinal }
      ].filter(c => c.value > 0).sort((a, b) => b.value - a.value);

      const adjustmentDirection = difference > 0 ? 1 : -1;
      let remainingDiff = Math.abs(difference);

      for (const category of categories) {
        if (remainingDiff === 0) break;
        const maxAdjust = Math.min(remainingDiff, 2);
        const adjustment = maxAdjust * adjustmentDirection;
        
        if (category.name === 'labor') laborFinal += adjustment;
        else if (category.name === 'material') materialFinal += adjustment;
        else equipmentFinal += adjustment;
        remainingDiff -= maxAdjust;
      }
    }

    const existingCOLabor = (job.labor_estimates || []).filter(e => e.is_change_order);
    const existingCOMaterial = (job.material_estimates || []).filter(e => e.is_change_order);
    const existingCOEquipment = (job.equipment_estimates || []).filter(e => e.is_change_order);

    const bidSheetNames = relevantBidSheets.map(bs => bs.name || "Bid Sheet").join(" + ");
    
    const laborEstimates = [
      {
        employee_id: `bid_sheet_labor_${Date.now()}`,
        employee_name: `Labor (${bidSheetNames})`,
        quantity: 1, unit: "lump", unit_price: Math.round(laborSubtotal), total: Math.round(laborSubtotal),
        description: `Base: ${formatCurrency(laborSubtotal)} + Burden: ${formatCurrency(totalBurdenAmount)} + Profit: ${formatCurrency(laborProfitAllocation)} = Total: ${formatCurrency(laborFinal)}`,
        from_bid_sheet: true,
      },
      ...existingCOLabor,
    ];

    const materialEstimates = [
      {
        material_id: `bid_sheet_material_${Date.now()}`,
        material_name: `Materials (${bidSheetNames})`,
        quantity: 1, unit: "lump", unit_price: Math.round(materialSubtotal), total: Math.round(materialSubtotal),
        description: `Base: ${formatCurrency(materialSubtotal)} + Tax: ${formatCurrency(totalTaxAmount)} + Profit: ${formatCurrency(materialProfitAllocation)} = Total: ${formatCurrency(materialFinal)}`,
        from_bid_sheet: true,
      },
      ...existingCOMaterial,
    ];

    const equipmentEstimates = [
      {
        equipment_id: `bid_sheet_equipment_${Date.now()}`,
        equipment_name: `Equipment (${bidSheetNames})`,
        quantity: 1, unit: "lump", unit_price: Math.round(equipmentSubtotal), total: Math.round(equipmentSubtotal),
        description: `Base: ${formatCurrency(equipmentSubtotal)} + Profit: ${formatCurrency(equipmentProfitAllocation)} = Total: ${formatCurrency(equipmentFinal)}`,
        from_bid_sheet: true,
      },
      ...existingCOEquipment,
    ];

    try {
      const response = await apiClient.put(
        `/jobs/${jobId}`,
        { labor_estimates: laborEstimates, material_estimates: materialEstimates, equipment_estimates: equipmentEstimates }
      );
      setJob(response.data);
      const finalTotal = laborFinal + materialFinal + equipmentFinal;
      toast.success(`Estimates populated from ${relevantBidSheets.length} bid sheet(s). Total: ${formatCurrency(finalTotal)}`);
      return true;
    } catch (error) {
      toast.error("Failed to populate estimates from bid sheet");
      return false;
    }
  }, [jobId, getAuthHeaders, setJob]);

  // Keep ref in sync for use by handleContractReceived
  populateRef.current = handlePopulateFromBidSheet;

  const handleUpdateAddendums = useCallback(async (count) => {
    try {
      const response = await apiClient.put(`/jobs/${jobId}`, { addendums_count: count });
      setJob(response.data);
      toast.success("Addendums updated");
    } catch (error) {
      toast.error("Failed to update addendums");
    }
  }, [jobId, getAuthHeaders, setJob]);

  return {
    // Job actions
    handleDelete,
    handleStatusChange,
    handleEditJob,
    handleContractReceived,
    // Labor
    handleSaveLaborEstimates,
    // Bid sheets
    handleSaveBidSheets,
    handleApproveCOBidSheet,
    handleRevokeCOApproval,
    // Materials
    handleSaveMaterialEstimates,
    // Invoices
    handleAddInvoice,
    handleEditInvoice,
    handleDeleteInvoice,
    handleToggleInvoicePaid,
    // Change orders
    handleAddChangeOrder,
    handleEditChangeOrder,
    handleDeleteChangeOrder,
    // Billings
    handleAddBilling,
    handleEditBilling,
    handleDeleteBilling,
    handleToggleBillingEdited,
    // Equipment
    handleSaveEquipmentEstimates,
    // Photos
    handlePhotoUpload,
    handleDeletePhoto,
    handleCaptionUpdate,
    // Notes
    handleAddNote,
    handleEditNote,
    handleDeleteNote,
    // Bidding GCs
    handleUpdateBiddingGCs,
    // Populate
    handlePopulateFromBidSheet,
    // Addendums
    handleUpdateAddendums,
  };
};

export default useJobHandlers;
